import { useContext } from 'react'
import { useQuery, useMutation, useQueryClient } from 'react-query'
import { apiGet, apiPost, apiPatch, apiDelete } from 'api'
import AuthContext from 'context/auth-context'

const getDashboardGridRows = async (token, id) => {
  const url = `dashboards/${id}/grid_rows`
  const data = await apiGet({ url, token })
  return data
}

export function useDashboardGridRows({ id, options = {} }) {
  const token = useContext(AuthContext)
  const queryFn = () => getDashboardGridRows(token, id)
  const queryKey = [`dashboards/${id}/grid_rows`]
  return useQuery(queryKey, queryFn, { enabled: !!id, ...options })
}

const createDashboardGridRow = async (token, id, params) => {
  const url = `dashboards/${id}/grid_rows`
  const objectName = 'dashboard_grid_row'
  const data = await apiPost({ url, token, objectName, params })
  return data
}

export function useCreateDashboardGridRow({ id, options }) {
  const token = useContext(AuthContext)
  const queryClient = useQueryClient()

  const mutationOptions = {
    onSuccess: (newGridRow) => {
      // Immediately update the cache with the new grid row
      queryClient.setQueryData([`dashboards/${id}/grid_rows`], (oldData) => {
        if (oldData) {
          return [...oldData, newGridRow]
        }
        return [newGridRow]
      })
      
      // Also invalidate to ensure fresh data on next fetch
      queryClient.invalidateQueries([`dashboards/${id}/grid_rows`])
      options && options.onSuccess && options.onSuccess()
    },
    ...(options || {})
  }

  return useMutation((params) => createDashboardGridRow(token, id, params), mutationOptions)
}

const updateDashboardGridRow = async (token, id, params) => {
  const url = `grid_rows/${id}`
  const objectName = 'dashboard_grid_row'
  const data = await apiPatch({ url, token, objectName, params })
  return data
}

const destroyDashboardGridRow = async (token, id) => {
  const url = `grid_rows/${id}`
  await apiDelete({ url, token })
}

export function useUpdateDashboardGridRow({ dashboardId, options }) {
  const token = useContext(AuthContext)
  const queryClient = useQueryClient()

  const mutationOptions = {
    ...(options || {}),
    onSuccess: (data, variables, context) => {
      queryClient.invalidateQueries([`dashboards/${dashboardId}/grid_rows`])
      if (options?.onSuccess) {
        options.onSuccess(data, variables, context)
      }
    }
  }

  return useMutation(
    ({ id, ...params }) => updateDashboardGridRow(token, id, params),
    mutationOptions
  )
}

export function useDestroyDashboardGridRow({ dashboardId, id, options }) {
  const token = useContext(AuthContext)
  const queryClient = useQueryClient()

  const mutationOptions = {
    ...(options || {}),
    onSuccess: (data, variables, context) => {
      queryClient.invalidateQueries([`dashboards/${dashboardId}/grid_rows`])
      if (options?.onSuccess) {
        options.onSuccess(data, variables, context)
      }
    }
  }

  return useMutation(() => destroyDashboardGridRow(token, id), mutationOptions)
}

export function useMoveGridRow({ dashboardId, options }) {
  const token = useContext(AuthContext)
  const queryClient = useQueryClient()

  const mutationOptions = {
    ...(options || {}),
    onSuccess: (data, variables, context) => {
      queryClient.invalidateQueries([`dashboards/${dashboardId}/grid_rows`])
      if (options?.onSuccess) {
        options.onSuccess(data, variables, context)
      }
    }
  }

  return useMutation(
    ({ id, new_position }) => updateDashboardGridRow(token, id, { new_position }),
    mutationOptions
  )
}